import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    XMarkIcon,
    ShieldExclamationIcon,
    DocumentTextIcon,
    TagIcon,
    ShieldCheckIcon,
    FolderIcon,
    FolderOpenIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    FingerPrintIcon,
    MagnifyingGlassIcon,
    ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { Button } from "../../ui";
import { riskCategory, riskSubCategory } from "../../../constants/AppEnums";
import { useSelector } from "react-redux";
import { getCustomerRiskControlOptionsApi } from "../../../services/axios/AuthApis/RisksManagementApis";

const riskStatusOptions = ["Open", "Mitigated", "Closed", "In-Progress"];

const Field = ({ label, required, error, icon: Icon, children }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
            {Icon && (
                <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
            )}
            {children}
        </div>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
);

const FieldTextarea = ({ label, required, error, icon: Icon, children }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
            {Icon && (
                <Icon className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none z-10" />
            )}
            {children}
        </div>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
);

const inputClass = (hasError, hasIcon = true) =>
    `w-full ${hasIcon ? "pl-9" : "pl-3"} pr-3 py-2.5 border ${hasError ? "border-red-400" : "border-gray-200"} rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50 focus:bg-white`;

const RiskForm = ({ risk = null, onSave, onCancel, isEditing = false }) => {
    const [formData, setFormData] = useState({
        risk_description: "",
        risk_category: "",
        risk_sub_category: "",
        risk_type: "",
        framework_control: "",
        framework_control_id: "",
        framework_id: "",
        existing_controls: "",
        risk_id: "",
        risk_status: "Open",
    });
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState("");
    const [subCategory, setSubCategory] = useState([]);
    const [controlOptions, setControlOptions] = useState([]);
    const [loadingControlOptions, setLoadingControlOptions] = useState(false);
    const [controlOptionsError, setControlOptionsError] = useState("");
    const [controlPickerOpen, setControlPickerOpen] = useState(false);
    const [controlSearch, setControlSearch] = useState("");
    const controlPickerRef = useRef(null);
    const saveInProgressRef = useRef(false);
    const userDetail = useSelector((state) => state.user);
    const userRole = userDetail?.user?.role || "";
    const isCustomerAdmin = userRole === "customer_admin";

    const categoryOptions = Array.isArray(riskCategory)
        ? riskCategory
        : Object.values(riskCategory || {});
    const unavailableSavedControl = formData.framework_control_id
        && !controlOptions.some((option) => option.id === formData.framework_control_id)
        ? {
            id: formData.framework_control_id,
            control_id: formData.framework_control,
            framework_name: "",
            framework_id: formData.framework_id,
            reference: formData.framework_control,
            selectable: false,
            legacy: true,
        }
        : null;
    const allControlOptions = unavailableSavedControl
        ? [...controlOptions, unavailableSavedControl]
        : controlOptions;
    const matchingControlOptions = allControlOptions.filter((option) => (
        `${option.control_id} ${option.framework_name}`.toLowerCase().includes(controlSearch.trim().toLowerCase())
    ));
    const filteredControlOptions = [
        ...matchingControlOptions.filter((option) => option.selectable),
        ...matchingControlOptions.filter((option) => !option.selectable),
    ];
    const selectedControl = controlOptions.find(
        (option) => option.id === formData.framework_control_id,
    );
    const selectedControlLabel = selectedControl
        ? `${selectedControl.control_id} - ${selectedControl.framework_name}${
            selectedControl.selectable ? "" : " (Framework not available for you)"
        }`
        : unavailableSavedControl
            ? `${formData.framework_control} (Framework not available for you)`
            : formData.framework_control || "";

    useEffect(() => {
        if (!controlPickerOpen) return undefined;
        const closeOnOutsideClick = (event) => {
            if (!controlPickerRef.current?.contains(event.target)) {
                setControlPickerOpen(false);
            }
        };
        document.addEventListener("mousedown", closeOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeOnOutsideClick);
    }, [controlPickerOpen]);

    useEffect(() => {
        if (!isCustomerAdmin) return undefined;
        let cancelled = false;
        setLoadingControlOptions(true);
        setControlOptionsError("");
        getCustomerRiskControlOptionsApi()
            .then((response) => {
                if (cancelled) return;
                const options = response.data || [];
                setControlOptions(options);
                if (isEditing && risk && !risk.framework_control_id && risk.framework_control) {
                    const legacyReference = risk.framework_control.toLowerCase();
                    const legacyMatch = options.find(
                        (option) => option.reference?.toLowerCase() === legacyReference
                            || option.control_id?.toLowerCase() === legacyReference
                    );
                    if (legacyMatch) {
                        setFormData((previous) => ({
                            ...previous,
                            framework_control_id: legacyMatch.id,
                            framework_id: legacyMatch.framework_id,
                        }));
                    }
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error("Could not load customer risk control references:", error);
                    setControlOptionsError("Control references could not be loaded. Try again before saving.");
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingControlOptions(false);
            });
        return () => { cancelled = true; };
    }, [isCustomerAdmin, isEditing, risk]);

    useEffect(() => {
        if (isEditing && risk) {
            setFormData({
                risk_description: risk.risk_description || "",
                risk_category: risk.category || "",
                risk_sub_category: risk.risk_sub_category || "",
                risk_type: risk.risk_type || "",
                framework_control: risk.framework_control || "",
                framework_control_id: risk.framework_control_id || "",
                framework_id: risk.framework_id || "",
                existing_controls: risk.existing_controls || "",
                risk_id: risk.risk_id || "",
                risk_status: risk.risk_status || "Open",
            });
            if (risk.category) {
                setSubCategory(riskSubCategory.filter((i) => i.category === risk.category));
            }
        } else {
            setFormData({
                risk_description: "",
                risk_category: "",
                risk_sub_category: "",
                risk_type: "",
                framework_control: "",
                framework_control_id: "",
                framework_id: "",
                existing_controls: "",
                risk_id: "",
                risk_status: "Open",
            });
        }
    }, [risk, isEditing]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.risk_description.trim()) newErrors.risk_description = "Risk description is required";
        if (!formData.risk_category) newErrors.risk_category = "Category is required";
        if (!formData.risk_sub_category) newErrors.risk_sub_category = "Sub category is required";
        if (isCustomerAdmin && !isEditing && !formData.framework_control_id) {
            newErrors.framework_control_id = "Select a control reference";
        } else if (!isCustomerAdmin && !formData.framework_control.trim()) {
            newErrors.framework_control = "Control reference is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (submissionError) setSubmissionError("");
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
        if (field === "risk_category") {
            setFormData((prev) => ({ ...prev, risk_sub_category: "", [field]: value }));
            setSubCategory(riskSubCategory.filter((i) => i.category === value));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saveInProgressRef.current) return;
        if (!validateForm()) return;
        saveInProgressRef.current = true;
        setSaving(true);
        try {
            await onSave(formData);
        } catch (error) {
            console.error("Error saving risk:", error);
            setSubmissionError(
                error?.response?.data?.message
                    || error?.response?.data?.error
                    || "The risk could not be saved. Please try again.",
            );
        } finally {
            saveInProgressRef.current = false;
            setSaving(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                {/* Backdrop */}
                <motion.div
                    key="backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
                    onClick={onCancel}
                />

                {/* Modal Panel */}
                <motion.div
                    key="modal"
                    initial={{ opacity: 0, y: -24, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 16, scale: 0.97 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh] overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center gap-4 px-6 py-5 bg-gradient-to-r from-primary-600 to-primary-500 rounded-t-2xl">
                        <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                            <ShieldExclamationIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-white leading-tight">
                                {isEditing ? "Edit Risk" : "New Risk"}
                            </h3>
                            <p className="text-xs text-primary-100 mt-0.5">
                                {isEditing ? "Update the risk details below" : "Add a new risk to the register"}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="flex-shrink-0 p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Body */}
                    <form onSubmit={handleSubmit} id="risk-form" className="flex-1 overflow-y-auto">
                        <div className="px-6 py-5 space-y-5">
                            {submissionError && (
                                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                    {submissionError}
                                </p>
                            )}

                            {/* Risk ID (admin only) */}
                            {userRole !== "customer_admin" && (
                                <Field label="Risk ID" icon={FingerPrintIcon}>
                                    <input
                                        type="text"
                                        value={formData.risk_id}
                                        onChange={(e) => handleChange("risk_id", e.target.value)}
                                        placeholder="e.g. RISK-001"
                                        className={inputClass(false)}
                                    />
                                </Field>
                            )}

                            {/* Risk Description */}
                            <FieldTextarea label="Risk Description" required error={errors.risk_description} icon={DocumentTextIcon}>
                                <textarea
                                    value={formData.risk_description}
                                    onChange={(e) => handleChange("risk_description", e.target.value)}
                                    placeholder="Describe the risk in detail…"
                                    rows={3}
                                    className={`w-full pl-9 pr-3 py-2.5 border ${errors.risk_description ? "border-red-400" : "border-gray-200"} rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none bg-gray-50 focus:bg-white`}
                                />
                            </FieldTextarea>

                            {/* Category & Sub Category */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="Category" required error={errors.risk_category} icon={FolderIcon}>
                                    <select
                                        value={formData.risk_category}
                                        onChange={(e) => handleChange("risk_category", e.target.value)}
                                        className={inputClass(!!errors.risk_category)}
                                    >
                                        <option value="">Select category…</option>
                                        {categoryOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.name}</option>
                                        ))}
                                    </select>
                                </Field>

                                <Field label="Sub Category" required error={errors.risk_sub_category} icon={FolderOpenIcon}>
                                    <select
                                        value={formData.risk_sub_category}
                                        onChange={(e) => handleChange("risk_sub_category", e.target.value)}
                                        disabled={!formData.risk_category}
                                        className={`${inputClass(!!errors.risk_sub_category)} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        <option value="">
                                            {formData.risk_category ? "Select sub category…" : "Select category first"}
                                        </option>
                                        {subCategory.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.name}</option>
                                        ))}
                                    </select>
                                </Field>
                            </div>

                            {/* Risk Type & Control Reference */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="Risk Type" icon={ExclamationTriangleIcon}>
                                    <input
                                        type="text"
                                        value={formData.risk_type}
                                        onChange={(e) => handleChange("risk_type", e.target.value)}
                                        placeholder="e.g. Operational, Strategic"
                                        className={inputClass(false)}
                                    />
                                </Field>

                                <div className="sm:col-span-2">
                                    <Field
                                        label="Control Reference"
                                        required={isCustomerAdmin ? !isEditing : true}
                                        error={errors.framework_control_id || errors.framework_control || controlOptionsError}
                                        icon={ShieldCheckIcon}
                                    >
                                    {isCustomerAdmin ? (
                                        <div ref={controlPickerRef} className="relative">
                                            <button
                                                type="button"
                                                aria-haspopup="listbox"
                                                aria-expanded={controlPickerOpen}
                                                aria-label="Select a control reference"
                                                disabled={loadingControlOptions || Boolean(controlOptionsError)}
                                                onClick={() => {
                                                    setControlPickerOpen((open) => !open);
                                                    setControlSearch("");
                                                }}
                                                className={`${inputClass(!!errors.framework_control_id)} flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60`}
                                            >
                                                <span className={selectedControlLabel ? "truncate" : "truncate text-gray-400"}>
                                                    {loadingControlOptions
                                                        ? "Loading controls…"
                                                        : selectedControlLabel || "Select a control…"}
                                                </span>
                                                <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                            </button>
                                            {controlPickerOpen && (
                                                <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xl">
                                                    <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
                                                        <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                                        <input
                                                            type="search"
                                                            aria-label="Search controls"
                                                            placeholder="Search controls…"
                                                            value={controlSearch}
                                                            onChange={(event) => setControlSearch(event.target.value)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === "Escape") setControlPickerOpen(false);
                                                            }}
                                                            className="min-w-0 flex-1 bg-transparent text-xs text-gray-700 outline-none placeholder:text-gray-400"
                                                        />
                                                    </div>
                                                    <div
                                                        role="listbox"
                                                        aria-label="Available control references"
                                                        className="max-h-72 overflow-y-auto divide-y divide-gray-100"
                                                    >
                                                        {filteredControlOptions.length === 0 ? (
                                                            <p className="px-3 py-3 text-center text-xs text-gray-400">
                                                                No matching controls.
                                                            </p>
                                                        ) : filteredControlOptions.map((option) => {
                                                            const unavailable = !option.selectable;
                                                            const label = option.legacy
                                                                ? option.control_id
                                                                : `${option.control_id} - ${option.framework_name}`;
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    role="option"
                                                                    aria-selected={option.id === formData.framework_control_id}
                                                                    key={option.id}
                                                                    disabled={unavailable}
                                                                    title={unavailable ? "Framework not available for you" : label}
                                                                    onClick={() => {
                                                                        setFormData((previous) => ({
                                                                            ...previous,
                                                                            framework_control_id: option.id,
                                                                            framework_id: option.framework_id,
                                                                            framework_control: option.reference || label,
                                                                        }));
                                                                        setErrors((previous) => ({
                                                                            ...previous,
                                                                            framework_control_id: "",
                                                                        }));
                                                                        setControlPickerOpen(false);
                                                                        setControlSearch("");
                                                                    }}
                                                                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors ${
                                                                        unavailable
                                                                            ? "cursor-not-allowed bg-gray-50 text-gray-400"
                                                                            : "cursor-pointer text-gray-700 hover:bg-primary-50"
                                                                    } ${option.id === formData.framework_control_id ? "bg-primary-50" : ""}`}
                                                                >
                                                                    <span
                                                                        aria-hidden="true"
                                                                        className={`h-3.5 w-3.5 shrink-0 rounded-full border ${
                                                                            option.id === formData.framework_control_id
                                                                                ? "border-primary-600 bg-primary-600"
                                                                                : unavailable
                                                                                    ? "border-gray-300 bg-gray-100"
                                                                                    : "border-gray-400 bg-white"
                                                                        }`}
                                                                    />
                                                                    <span className="min-w-0 truncate">
                                                                        <span className={`font-semibold ${unavailable ? "text-gray-400" : "text-gray-800"}`}>
                                                                            {option.control_id}
                                                                        </span>
                                                                        <span className={unavailable ? "text-gray-400" : "text-gray-500"}>
                                                                            {option.framework_name && ` - ${option.framework_name}`}
                                                                        </span>
                                                                        {unavailable && (
                                                                            <span className="text-gray-400">
                                                                                {" (Framework not available for you)"}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            {isEditing && !formData.framework_control_id && formData.framework_control && (
                                                <p className="mt-1 text-xs text-amber-700">
                                                    Saved legacy reference: {formData.framework_control}. Choose a catalogue control to replace it.
                                                </p>
                                            )}
                                            {!isEditing && (
                                                <p className="mt-1 text-xs text-gray-500">
                                                    Controls from other frameworks are shown but unavailable for this customer.
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            value={formData.framework_control}
                                            onChange={(e) => handleChange("framework_control", e.target.value)}
                                            placeholder="e.g. ISO/IEC 27001 A.9.2.3"
                                            className={inputClass(!!errors.framework_control)}
                                        />
                                    )}
                                    </Field>
                                </div>
                            </div>

                            {/* Existing Controls */}
                            <FieldTextarea label="Existing Controls" icon={DocumentTextIcon}>
                                <textarea
                                    value={formData.existing_controls}
                                    onChange={(e) => handleChange("existing_controls", e.target.value)}
                                    placeholder="Describe existing controls in place…"
                                    rows={3}
                                    className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none bg-gray-50 focus:bg-white"
                                />
                            </FieldTextarea>

                            {/* Risk Status (customer_admin + editing only) */}
                            {userRole === "customer_admin" && isEditing && (
                                <Field label="Risk Status" icon={CheckCircleIcon}>
                                    <select
                                        value={formData.risk_status}
                                        onChange={(e) => handleChange("risk_status", e.target.value)}
                                        className={inputClass(false)}
                                    >
                                        {riskStatusOptions.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </Field>
                            )}

                        </div>
                    </form>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-2xl">
                        <p className="text-xs text-gray-400">
                            <span className="text-red-400">*</span> Required fields
                        </p>
                        <div className="flex items-center gap-3">
                            <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                form="risk-form"
                                variant="primary"
                                size="sm"
                                disabled={saving || (isCustomerAdmin && (loadingControlOptions || Boolean(controlOptionsError)))}
                                loading={saving}
                            >
                                {saving ? "Saving…" : isEditing ? "Update Risk" : "Create Risk"}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default RiskForm;
